import { useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { apiHelpers } from '../../services/api';
import toast from 'react-hot-toast';
import {
  CloudArrowUpIcon,
  DocumentTextIcon,
  ArrowLeftIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

export default function DataUpload() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialType = searchParams.get('type') || 'csv';
  
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [name, setName] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  
  const onDrop = useCallback(async (acceptedFiles) => {
    const uploadedFile = acceptedFiles[0];
    if (!uploadedFile) return;
    
    setFile(uploadedFile);
    setName(uploadedFile.name.replace(/\.[^/.]+$/, ''));
    setIsUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', uploadedFile);
      formData.append('name', uploadedFile.name);
      
      const response = await apiHelpers.uploadFile(formData);
      setPreview(response.data);
      setStep(2);
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setIsUploading(false);
    }
  }, []);
  
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
    },
    maxFiles: 1,
    maxSize: 100 * 1024 * 1024 // 100MB
  });
  
  const handleImport = async () => {
    if (!preview?.dataSource?._id) {
      toast.error('No data source created');
      return;
    }
    
    setIsUploading(true);
    
    try {
      await apiHelpers.importData(preview.dataSource._id, 'full');
      toast.success('Import started!');
      navigate(`/data/${preview.dataSource._id}`);
    } catch (error) {
      toast.error('Failed to start import');
    } finally {
      setIsUploading(false);
    }
  };
  
  return (
    <div className="max-w-4xl mx-auto animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button 
          onClick={() => navigate('/data')}
          className="p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800"
        >
          <ArrowLeftIcon className="w-5 h-5 text-surface-600 dark:text-surface-400" />
        </button>
        <div>
          <h1 className="text-2xl font-display font-bold text-surface-900 dark:text-white">
            Import Data
          </h1>
          <p className="text-surface-500 dark:text-surface-400">
            Upload a file to create a new data source
          </p>
        </div>
      </div>
      
      {/* Progress steps */}
      <div className="flex items-center gap-4 mb-8">
        {[
          { num: 1, label: 'Upload File' },
          { num: 2, label: 'Preview Data' },
          { num: 3, label: 'Import' }
        ].map((s, i) => (
          <div key={s.num} className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-medium text-sm ${
              step >= s.num 
                ? 'bg-primary-600 text-white' 
                : 'bg-surface-200 dark:bg-surface-700 text-surface-500'
            }`}>
              {step > s.num ? <CheckCircleIcon className="w-5 h-5" /> : s.num}
            </div>
            <span className={`text-sm ${step >= s.num ? 'text-surface-900 dark:text-white' : 'text-surface-500'}`}>
              {s.label}
            </span>
            {i < 2 && <div className="w-12 h-px bg-surface-200 dark:bg-surface-700" />}
          </div>
        ))}
      </div>
      
      {/* Step 1: Upload */}
      {step === 1 && (
        <div className="card p-8">
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${
              isDragActive 
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-950' 
                : 'border-surface-300 dark:border-surface-700 hover:border-primary-400'
            }`}
          >
            <input {...getInputProps()} />
            
            {isUploading ? (
              <div>
                <div className="w-12 h-12 mx-auto mb-4 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
                <p className="text-surface-600 dark:text-surface-400">Uploading...</p>
              </div>
            ) : (
              <>
                <CloudArrowUpIcon className="w-12 h-12 mx-auto mb-4 text-surface-400" />
                <p className="text-lg font-medium text-surface-900 dark:text-white mb-2">
                  {isDragActive ? 'Drop your file here' : 'Drag & drop a file here'}
                </p>
                <p className="text-surface-500 dark:text-surface-400 mb-4">
                  or click to browse
                </p>
                <p className="text-sm text-surface-400">
                  Supports CSV, XLS, XLSX • Max 100MB
                </p>
              </>
            )}
          </div>
        </div>
      )}
      
      {/* Step 2: Preview */}
      {step === 2 && preview && (
        <div className="space-y-6">
          <div className="card p-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                <DocumentTextIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
              <div className="flex-1">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="text-lg font-medium bg-transparent border-b border-transparent hover:border-surface-300 focus:border-primary-500 outline-none text-surface-900 dark:text-white w-full"
                />
                <p className="text-sm text-surface-500">
                  {preview.schema?.columns?.length} columns • {preview.schema?.totalRows} rows
                </p>
              </div>
            </div>
            
            {/* Schema preview */}
            <div className="mb-6">
              <h3 className="font-medium text-surface-900 dark:text-white mb-3">Detected Schema</h3>
              <div className="flex flex-wrap gap-2">
                {preview.schema?.columns?.map((col, i) => (
                  <div key={i} className="bg-surface-100 dark:bg-surface-800 rounded-lg px-3 py-2 text-sm">
                    <span className="text-surface-900 dark:text-white font-medium">{col.name}</span>
                    <span className="text-surface-500 ml-2">{col.displayType}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Data preview */}
            <h3 className="font-medium text-surface-900 dark:text-white mb-3">Data Preview</h3>
            <div className="overflow-x-auto rounded-lg border border-surface-200 dark:border-surface-700">
              <table className="w-full text-sm">
                <thead className="bg-surface-50 dark:bg-surface-800">
                  <tr>
                    {preview.schema?.columns?.map((col, i) => (
                      <th key={i} className="px-4 py-2 text-left font-medium text-surface-600 dark:text-surface-400 whitespace-nowrap">
                        {col.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.preview?.map((row, i) => (
                    <tr key={i} className="border-t border-surface-100 dark:border-surface-800">
                      {preview.schema?.columns?.map((col, j) => (
                        <td key={j} className="px-4 py-2 text-surface-700 dark:text-surface-300 whitespace-nowrap">
                          {String(row[col.name] ?? '')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <button onClick={() => setStep(1)} className="btn-secondary">
              Back
            </button>
            <button onClick={handleImport} disabled={isUploading} className="btn-primary">
              {isUploading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
              ) : null}
              Import Data
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

